import type { FriendEntry, WinDrawLoss } from "@golazo/shared";
import { t } from "../i18n/index.js";

/**
 * Como se cuenta un amigo en una linea: donde esta, desde cuando no viene y
 * cuanto le llevas. Lo usan el lobby y el panel de amigos del menu, que
 * tienen que decir lo mismo con las mismas palabras.
 */

export function lastSeenText(ms: number, now = Date.now()): string {
  const mins = Math.floor((now - ms) / 60000);
  if (mins < 60) return t("hace {n} min", { n: Math.max(1, mins) });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("hace {n} h", { n: hours });
  return t("hace {n} días", { n: Math.floor(hours / 24) });
}

/**
 * Donde esta, con el detalle que da ganas de ir (como la lista de amigos de
 * CS2: "Jugando · Mirage 5:3"). Aqui el servidor no sabe el marcador —no
 * simula—, pero si cuando empezo: "min 3".
 */
export function whereText(f: FriendEntry, now = Date.now()): string {
  if (f.status === "playing") {
    if (f.playingSince !== undefined) {
      const min = Math.max(1, Math.floor((now - f.playingSince) / 60_000) + 1);
      return t("Jugando en {sala} · min {m}", { sala: f.roomName ?? "", m: min });
    }
    return t("Jugando en {sala}", { sala: f.roomName ?? "" });
  }
  if (f.status === "room") return t("En {sala} · {modo}", { sala: f.roomName ?? "", modo: f.roomMode ?? "" });
  if (f.status === "online") {
    return f.party && f.party.size >= 2 ? t("En un grupo ({n}/{max})", { n: f.party.size, max: f.party.max }) : t("Conectado");
  }
  return lastSeenText(f.lastSeen, now);
}

export interface RecordChip {
  /** "5-3": ganadas-perdidas (los empates van en el titulo). */
  text: string;
  title: string;
  tone: "ahead" | "behind" | "even";
}

/**
 * Lo que llevais uno contra otro, en corto. Sale de los partidos que rehizo
 * el arbitro del servidor, no del perfil de cada uno: aqui no se puede mentir.
 */
export function vsChip(f: Pick<FriendEntry, "nickname" | "vs">): RecordChip | null {
  const r = f.vs;
  if (!r || r[0] + r[1] + r[2] === 0) return null;
  const [w, d, l] = r;
  return {
    text: `${w}-${l}`,
    title: t("Contra {nick}: {w} victorias, {d} empates, {l} derrotas", { nick: f.nickname, w, d, l }),
    tone: w > l ? "ahead" : w < l ? "behind" : "even",
  };
}

/** Una frase para el pique: "Le llevas 5-3", "Te lleva 4-1", "Vais 2-2". */
export function rivalryLine(nickname: string, r: WinDrawLoss | undefined): string | null {
  if (!r || r[0] + r[1] + r[2] === 0) return null;
  const [w, , l] = r;
  if (w > l) return t("A {nick} le llevas {w}-{l}", { nick: nickname, w, l });
  if (w < l) return t("{nick} te lleva {l}-{w}", { nick: nickname, w, l });
  return t("Con {nick} vais {w}-{l}", { nick: nickname, w, l });
}

/** Juntos en el mismo equipo: "Juntos: 7 de 10". */
export function togetherLine(r: WinDrawLoss | undefined): string | null {
  if (!r) return null;
  const played = r[0] + r[1] + r[2];
  if (played === 0) return null;
  return t("Juntos: {w} victorias de {n}", { w: r[0], n: played });
}
